import React, { useState, useEffect } from 'react';
import { XCircle, Upload, Plus, Trash2, FileText, User, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';

export default function SubirDocumentosModal({ solicitud, isArchivero = false, onClose, onSuccess }) {
  const [fileInputs, setFileInputs] = useState([{ id: Date.now(), file: null }]);
  const [notificar, setNotificar] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleAddInput = () => {
    setFileInputs(prev => [...prev, { id: Date.now(), file: null }]);
  };

  const handleRemoveInput = (id) => {
    setFileInputs(prev => prev.filter(input => input.id !== id));
  };

  const handleFileChange = (id, file) => {
    setFileInputs(prev => prev.map(input => input.id === id ? { ...input, file } : input));
  };

  const handleSubmit = async () => {
    const validFiles = fileInputs.filter(input => input.file).map(input => input.file);
    if (validFiles.length === 0) {
      toast.error('Selecciona al menos un archivo para subir.');
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      validFiles.forEach(f => fd.append('documentos[]', f));
      // La API de archivero usa enviar_correo en lugar de notificar
      if (isArchivero) {
        fd.append('enviar_correo', notificar ? '1' : '0');
      } else {
        fd.append('notificar', notificar ? '1' : '0');
      }
      if (mensaje.trim()) {
        fd.append('mensaje', mensaje.trim());
      }

      const url = isArchivero 
        ? `/archivero/solicitudes/${solicitud.id}/documentos`
        : `/solicitudes/${solicitud.id}/documentos`;

      const { data } = await api.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const msg = data.notificado
        ? `${validFiles.length} documento(s) subidos y ciudadano notificado.`
        : `${validFiles.length} documento(s) subidos correctamente.`;
        
      toast.success(msg);
      onSuccess();
    } catch (err) {
      toast.error('Error al subir los documentos.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div className="modal-content" style={{ maxWidth: 650, width: '100%', padding: '1.5rem', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Encabezado */}
        <div className="modal-header" style={{ marginBottom: '1.2rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-color)' }}>
          <h2 style={{ fontSize: '1.2rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <Upload size={20} /> Subir Documentos
          </h2>
          <button className="modal-close" onClick={onClose} disabled={uploading}>
            <XCircle size={22} />
          </button>
        </div>

        {/* Visor de Solicitud (Resumen) */}
        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem', border: '1px solid var(--border-color)' }}>
          <h3 style={{ fontSize: '0.9rem', color: 'var(--text-main)', margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} /> Resumen de Solicitud #{solicitud.numero_seguimiento || 'S/N'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            <div>
              <strong><User size={12}/> Ciudadano:</strong> {solicitud.nombre_solicitante}
            </div>
            <div>
              <strong>RUT:</strong> {solicitud.rut_solicitante}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>Email:</strong> {solicitud.email}
            </div>
            <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
              {isArchivero ? (
                <>
                  <strong>Detalle de Archivero (1):</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: '1.2rem' }}>
                    <li>
                      {solicitud.tipo_solicitud === 'escritura' ? 'Copia de Escritura' : 'Copia de Protocolización'} - 
                      Notaría: {solicitud.notaria === 'primera_san_carlos' ? 'Primera' : 'Segunda'}, 
                      Año: {solicitud.anio || solicitud.fecha}, 
                      {solicitud.tipo_solicitud === 'escritura' 
                        ? ` Rep: ${solicitud.repertorio}` 
                        : ` Prot: ${solicitud.numero_protocolizacion}`}
                    </li>
                  </ul>
                </>
              ) : (
                <>
                  <strong>Ítems Solicitados ({solicitud.items?.length || 0}):</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: '1.2rem' }}>
                    {solicitud.items?.map(item => (
                      <li key={item.id}>{item.tipo_documento} - {item.registro || 'Sin registro asignado'}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Archivos Ya Subidos (Si hay) */}
        {solicitud.documentos && solicitud.documentos.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.5rem' }}>
              Archivos ya subidos
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {solicitud.documentos.map(doc => (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.7rem', background: '#f8fafc', borderRadius: 7, border: '1px solid var(--border-color)', fontSize: '0.8rem' }}>
                  <FileText size={13} color="var(--copper)" />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nombre_original}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Lista de Inputs de Archivos */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.5rem' }}>
            Archivos a adjuntar
          </label>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {fileInputs.map((input, index) => (
              <div key={input.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ 
                  flex: 1, 
                  border: '1px solid var(--border-color)', 
                  borderRadius: 6, 
                  padding: '0.4rem 0.6rem',
                  display: 'flex',
                  alignItems: 'center',
                  background: 'var(--bg-surface)'
                }}>
                  <Paperclip size={14} style={{ marginRight: 8, color: 'var(--copper)' }} />
                  <input 
                    type="file" 
                    onChange={(e) => handleFileChange(input.id, e.target.files[0])}
                    style={{ flex: 1, fontSize: '0.85rem' }} 
                  />
                </div>
                {fileInputs.length > 1 && (
                  <button 
                    className="btn btn-sm" 
                    style={{ background: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5', padding: '0.4rem 0.6rem' }}
                    onClick={() => handleRemoveInput(input.id)}
                    title="Eliminar archivo"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button 
            className="btn btn-sm" 
            style={{ marginTop: '0.8rem', display: 'flex', alignItems: 'center', gap: 6, background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
            onClick={handleAddInput}
          >
            <Plus size={14} /> Agregar otro archivo
          </button>
        </div>

        {/* Mensaje Opcional */}
        <div style={{ marginBottom: '1.2rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.4rem' }}>
            Mensaje opcional para el ciudadano
          </label>
          <textarea
            value={mensaje}
            onChange={e => setMensaje(e.target.value)}
            placeholder="Escribe un mensaje que aparecerá en el correo de notificación y en el historial..."
            rows={3}
            style={{
              width: '100%',
              fontSize: '0.85rem',
              padding: '0.6rem',
              borderRadius: 6,
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Opciones Adicionales */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '0.75rem 1rem',
            borderRadius: 8,
            border: `1.5px solid ${notificar ? '#b87333' : 'var(--border-color)'}`,
            background: notificar ? 'rgba(184,115,51,0.07)' : 'var(--bg-body)',
            cursor: 'pointer', userSelect: 'none',
            transition: 'all 0.18s ease',
          }}>
            <input
              type="checkbox"
              checked={notificar}
              onChange={e => setNotificar(e.target.checked)}
              style={{ accentColor: '#b87333', width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={{ fontSize: '0.85rem', lineHeight: 1.3 }}>
              ✉️ <strong style={{ color: notificar ? '#b87333' : 'inherit' }}>
                Notificar al ciudadano
              </strong>
              <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Recibe un correo avisando que se subieron nuevos documentos.
              </span>
            </span>
          </label>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={uploading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {uploading ? (
              <><span className="spinner" style={{ width: 16, height: 16, borderTopColor: '#fff' }} /> Enviando...</>
            ) : (
              <><Upload size={16} /> Enviar Archivos</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
